import { Typography } from '@mui/material';
import Box from '@mui/material/Box';
import * as React from "react"
import { Autoplay, EffectCoverflow, EffectFade, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import Fluid from 'components/Fluid'
import Link from 'components/Link'
import LongArrowButton from 'components/LongArrowButton'
import { ReactComponent as ArrowLeft } from 'images/icons/arrow_left_long.svg';
import { ReactComponent as ArrowRight } from 'images/icons/arrow_right_long.svg';
import { ReactComponent as ArrowUpRight } from 'images/icons/arrow_up_right.svg';

import 'swiper/css';
import 'swiper/css/effect-coverflow';
import 'swiper/css/effect-fade';

const ImageSlider = ({ slider, layout = 'gallery', title, description, link, linkText, flush, background, backgroundImage, backgroundProps, padding, isFeatured = false, ...props }) => {
	const slides = slider?.slides;
	const [galleryLoop, setGalleryLoop] = React.useState([1]);
	const paginationRef = React.useRef(null);
	const swiperRef = React.useRef(null);
	const [swiperKey, setSwiperKey] = React.useState(0);

	let sliderParams = {};

	const inlineStyle = {
		...(backgroundImage ? {
			backgroundImage: `url(${process.env.CDN_DOMAIN || ''}${backgroundImage}?format=webp&max-w=1920&quality=70)`,
			...(backgroundProps ? {
				...(backgroundProps.indexOf('cover') !== -1 ? {
					backgroundSize: 'cover',
				} : null),
				...(backgroundProps.indexOf('fixed') !== -1 ? {
					backgroundAttachment: 'fixed',
				} : null),
				...(backgroundProps.indexOf('repeat') !== -1 ? {
					backgroundRepeat: 'repeat',
				} : { backgroundRepeat: 'no-repeat' }),
			} : null),
		} : null),
	}

	switch (layout) {
		case 'fullwidth':
			sliderParams = {
				modules: [Autoplay, Pagination, EffectFade],
				slidesPerView: 1,
				slidesPerGroup: 1,
				pagination: {
					clickable: true,
					type: 'bullets',
					bulletClass: 'imageSlider__bullet',
					bulletActiveClass: 'imageSlider__bullet-active',
					bulletElement: 'button',
					modifierClass: 'imageSlider__',
					el: paginationRef?.current,
					renderBullet: function (index, className) {
						return '<button aria-label="Slide ' + (index + 1) + '" class="' + className + '"></button>';
					},
				},
				className: 'imageSlider',
				effect: 'fade',
				fadeEffect: {
					crossFade: true
				},
				centeredSlides: false,
				spaceBetween: '0',
				speed: 1000,
				loop: true,
				autoplay: {
					pauseOnMouseEnter: true,
				}
			}
			break;
		case 'gallery':
			sliderParams = {
				modules: [Autoplay, EffectCoverflow],
				slidesPerView: 1.15,
				slidesPerGroup: 1,
				pagination: false,
				className: 'imageSlider',
				effect: 'coverflow',
				centeredSlides: true,
				spaceBetween: '0',
				fadeEffect: false,
				coverflowEffect: {
					rotate: 0,
					depth: 500,
					scale: .95,
					slideShadows: false,
				},
				speed: 1400,
				loop: true,
				autoplay: {
					delay: 3000,
					pauseOnMouseEnter: true,
				},
				breakpoints: {
					2000: {
						slidesPerView: 1,
					}
				}
			}
			break;
		case 'gallery-fade':
			sliderParams = {
				modules: [Pagination, Autoplay, EffectFade],
				slidesPerView: 1,
				slidesPerGroup: 1,
				effect: 'fade',
				fadeEffect: {
					crossFade: true,
				},
				loop: true,
				speed: 1000,
				className: 'imageSlider imageSlider__galleryFade',
				autoplay: {
					pauseOnMouseEnter: true,
				},
				pagination: {
					clickable: true,
					type: 'bullets',
					bulletClass: 'imageSlider__galleryFade_bullet',
					bulletActiveClass: 'imageSlider__galleryFade_bullet-active',
					bulletElement: 'button',
					modifierClass: 'imageSlider__galleryFade_',
					enabled: true,
				},
			};
			break;
		default:
			sliderParams = {
				modules: [Autoplay, EffectCoverflow],
				slidesPerView: 1.5,
				slidesPerGroup: 1,
				pagination: false,
				className: 'imageSlider',
				effect: 'coverflow',
				centeredSlides: true,
				spaceBetween: '0',
				fadeEffect: false,
				coverflowEffect: {
					rotate: 0,
					depth: 500,
					scale: 0.65,
					slideShadows: false,
				},
				speed: 300,
				loop: true,
				autoplay: {
					pauseOnMouseEnter: true,
				}
			}
	}

	React.useEffect(() => {
		if (layout === 'gallery') {
			let slideCount = slides.length
			let i = slides.length;
			while (i < (sliderParams?.slidesPerView + sliderParams?.slidesPerGroup + 2)) {
				setGalleryLoop(g => [...g, 1]);
				i = i + slideCount;
			}
			return;
		}

		setGalleryLoop([1]);
	}, [layout, slides, sliderParams?.slidesPerView, sliderParams?.slidesPerGroup, setGalleryLoop]);

	React.useEffect(() => {
		if (paginationRef.current) {
			setSwiperKey(s => s + 1);
		}
	}, [layout]);

	if (!slides || slides?.length === 0) return null;

	return (
		<Box
			sx={(Object.keys(inlineStyle).length > 0 ? inlineStyle : null)}
			className={[
				'imageSlider__container',
				`${layout}-layout`,
				(padding ? 'padding-normal' : ''),
				(flush ? 'margin-flush' : ''),
				(background || '')
			].join(' ')}
		>
			{(title || link || description) &&
				<Fluid layout={{ xs: '12', md: '10', lg: '8' }} justifyContent={'center'} className="imageSlider__title_section">
					<Box className="imageSlider__title_container">
						{title && <Typography className="imageSlider__title" component="div" variant="h3">{title}</Typography>}
						{description && <Typography className="imageSlider__description" component="div" variant="large">{description}</Typography>}
						{link && <Link className="imageSlider__title__link" variant="button" component={LongArrowButton} href={link}>{linkText ? linkText : 'Learn More'}</Link>}
					</Box>
				</Fluid>
			}
			<Swiper
				modules={sliderParams?.modules}
				slidesPerView={sliderParams?.slidesPerView}
				slidesPerGroup={sliderParams?.slidesPerGroup}
				pagination={sliderParams?.pagination}
				effect={sliderParams?.effect}
				fadeEffect={sliderParams?.fadeEffect ?? false}
				coverflowEffect={sliderParams?.coverflowEffect ?? false}
				spaceBetween={sliderParams?.spaceBetween}
				centeredSlides={sliderParams?.centeredSlides}
				autoHeight={sliderParams?.autoHeight ?? false}
				breakpoints={sliderParams?.breakpoints}
				autoplay={sliderParams?.autoplay ?? false}
				// autoplay={false}
				loop={sliderParams?.loop ?? true}
				className={sliderParams?.className}
				speed={sliderParams?.speed ?? 1000}
				key={`${swiperKey}-${layout}`}
				onBeforeInit={(swiper) => {
					swiperRef.current = swiper;
				}}
				onBreakpoint={(swiper) => {
					if (layout === 'gallery') {
						swiper.params.coverflowEffect.scale =
							Number(swiper.currentBreakpoint) >= 1920 ? 1.25 : 0.95;
					}
				}}
			>
				{layout === 'fullwidth' && slides?.length && slides.map((slide, index) => {
					return (
						<SwiperSlide key={index} className="imageSlider__slide">
							<Box sx={{
								backgroundImage: `url('${slide?.image}?format=webp&max-w=1920&quality=70')`,
							}}
								className="imageSlider__inner"
							>
								<Fluid layout={{ xs: '9 3', md: '5 1' }} justifyContent={{ xs: 'start', md: 'space-between' }} className="imageSlider__grid">
									<div className="imageSlider__content_container">
										{slide?.subtitle && <p className="imageSlider__subtitle">{slide.subtitle}</p>}
										<p className="imageSlider__title">{slide?.title}</p>
									</div>
									{slide?.link && <div className="imageSlider__link_holder">
										<Link className="imageSlider__link" href={slide?.link} aria-label={`Learn more about ${slide?.title}`}><ArrowUpRight /></Link>
									</div>}
								</Fluid>
							</Box>
						</SwiperSlide>
					)
				})}
				{layout === 'gallery' && slides?.length && galleryLoop.map(() => (
					slides.map((slide, index) => {
						return (
							<SwiperSlide key={index} className="imageSlider__slide">
								<Box className="imageSlider__gallery_slide__inner"
								>
									<Fluid layout="10" justifyContent="center" sx={{ width: "100%" }}>
										<Box
											className="imageSlider__gallery_slide__background"
											sx={{
												backgroundImage: `url('${slide?.image}?format=webp&max-w=1920&quality=70')`,
											}} />
									</Fluid>
									<Fluid layout={{ xs: '12 12', md: '7 3' }} justifyContent="center" bleed={'left'} className="imageSlider__gallery_slide__grid">
										<div className="imageSlider__content_container">
											<p className="imageSlider__title">{slide?.title}</p>
											{slide?.subtitle && <p className={`imageSlider__subtitle${isFeatured ? ' imageSlider__subtitle--normal' : ''}`}>{slide.subtitle}</p>}
										</div>
										{slide?.link && <div className="imageSlider__link_holder">
											<Link className="imageSlider__link" href={slide?.link}>{slide?.linkText ? slide?.linkText : 'Add To My Morning'}</Link>
										</div>}
									</Fluid>
								</Box>
							</SwiperSlide>
						)
					})
				))}
				{layout === 'gallery-fade' && slides?.length && slides.map((slide, index) => {
					return (
						<SwiperSlide key={index} className="imageSlider__galleryFade_slide">
							<Box
								className="imageSlider__galleryFade_slide_background"
								sx={{
									backgroundImage: `url('${slide?.image}?format=webp&max-w=1920&quality=70')`,
								}}
							/>
						</SwiperSlide>
					)
				})}
				{(layout === 'gallery-fade' && slides?.length > 1) && (
					<div className="imageSlider__galleryFade_navigation">
						<button onClick={() => swiperRef.current?.slidePrev()} aria-label="Previous slide">
							<ArrowLeft />
						</button>
						<button onClick={() => swiperRef.current?.slideNext()} aria-label="Next slide">
							<ArrowRight />
						</button>
					</div>
				)}
			</Swiper>
			{layout === 'fullwidth' && slides?.length && <Fluid layout={{ xs: '12', md: '6 1' }} justifyContent={{ xs: 'center', md: 'end' }} className="imageSlider__pagination">
				<div ref={paginationRef} />
				<div className="spacer" />
			</Fluid>}
		</Box>
	)
}

export default ImageSlider;